import { createFormData, getFormDataConfig, JsonParseReviverFunction, postFormData, REQUEST_MODULE, REQUEST_OPERATION } from "../core/request";
import { AggregateStoreUniqueId, IAggregateStoreDefinitionInfo, IAggregateStoreGroup, IAggregateStoreIdsResponse, IAggregateStoreParams, IAggregateStoreResponse, IAggregateStoreUpdateOperation } from "../type/aggregateStore";
import { ATTR_NAME, C8AttributeId, C8NodeType } from "../type/node";
import { C8AttributeType, cnl } from "../type/value";
import { getDataStoreComponents, queryDataStoreInfo } from "./dataStoreService";

// reference: https://centric8.atlassian.net/wiki/spaces/DEV/pages/3672703008/Aggregate+Stores#Querying-an-Aggregate-Store

/** default limit to number of results to return from aggregate store */
// TODO: need to determine proper limit - for 7.5, cap at large result so it does not return an infinite result set;
// for 7.5, the alternative is for the customer to configure Delayed Search for their attribute
export const AGGREGATE_STORE_LIMIT = 100000;

const DEFAULT_RESULT_KEYS_KEY = "";

const DISABLED_IDS_KEY = "disabledIds";
const RESULT_KEYS_KEY = "resultKeys";
const ID_EMPTY: AggregateStoreUniqueId = "";
const ID_IN_PROGRESS: AggregateStoreUniqueId = "???";

type AggregateStoreIdKey = string;
type AggregateStoreResponseKey = string;
type AggregateStoreResponseMap = Record<AggregateStoreUniqueId, IAggregateStoreResponse>;

/** JSON parse reviver function for QueryAggregateStore operation */
const queryAggregateStoreReviver: JsonParseReviverFunction = (key, value) => {
	switch (key) {
		case DISABLED_IDS_KEY: return new Set(value);
		case RESULT_KEYS_KEY: return new Map(Object.entries(value));
		default: return value;
	}
};

/** cache of aggregate store respones per node type/attribute id */
let cache: Record<AggregateStoreResponseKey, AggregateStoreResponseMap> = {};

/** cache of aggregate store ids per node url/attribute id */
let cacheDragIds: Record<AggregateStoreIdKey, AggregateStoreUniqueId> = {};

/** cache of drag-fill operation definition per node url/attribute id */
let cacheDragOperations: Record<AggregateStoreIdKey, IAggregateStoreUpdateOperation> = {};

/** return query XML in aggregate store results */
let returnQueryXML: boolean = false;

/** return true if response contains multiple attributes for results (such as when using slim cards) */
export function aggregateStoreResponseHasMultipleKeys(res: IAggregateStoreResponse): boolean {
	if (res.resultKeys) {
		for (const list of res.resultKeys.values()) {
			if (list.length > 1) {
				return true;
			}
		}
	}
	return false;
}

/** cache unique id for aggregate store of given url and attribute */
function cacheAggregateStoreDragFillId(nodeUrl: cnl, attributeId: C8AttributeId, dragFillId?: AggregateStoreUniqueId, operation?: IAggregateStoreUpdateOperation): void {
	const key = getAggregateStoreDragFillIdCacheKey(nodeUrl, attributeId);
	cacheDragIds[key] = dragFillId || ID_EMPTY;
	cacheDragOperations[key] = operation;
}

/** return key to use in aggregate store cache */
export function getAggregateStoreCacheKey(
	nodeType: C8NodeType,
	attributeId: C8AttributeId,
	reverseType?: C8NodeType,
	reverseAttributeId?: C8AttributeId,
): AggregateStoreResponseKey {
	return reverseType ? `${reverseType}^${reverseAttributeId}<` : `${nodeType}^${attributeId}`;
}

/** return key to use in aggregate store id cache */
function getAggregateStoreDragFillIdCacheKey(nodeUrl: cnl, attributeId: C8AttributeId): AggregateStoreIdKey {
	return `${nodeUrl}^${attributeId}`;
}

/**
 * Query aggregate store unique ids for an attribute of the given urls; the ids will be cached
 * until cleared through clearAggregateStoreIdCache. This information is used to support drag-fill operations -
 * a ref attribute value can be dragged from one url to another only if their aggregate store unique ids match
 */
export async function cacheAggregateStoreDragFillIds(sourceUrls: cnl[], attributeId: C8AttributeId): Promise<void> {
	const newUrls = sourceUrls.filter((url) => cacheDragIds[getAggregateStoreDragFillIdCacheKey(url, attributeId)] === undefined);

	if (!newUrls.length) {
		return;
	}

	try {
		// indicate ids are being queried so that they will not be queried again while in progress
		for (const url of newUrls) {
			cacheAggregateStoreDragFillId(url, attributeId, ID_IN_PROGRESS);
		}

		const response = await postFormData<IAggregateStoreIdsResponse>(createFormData({
			[REQUEST_MODULE]: "DataStore",
			[REQUEST_OPERATION]: "GetAggregateStoreDragFillIds",
			AttributeId: attributeId,
			SourceUrl: newUrls,
		}));
		const { groups } = response;

		// cache ids
		for (const [dragFillId, { data, updateOperation }] of Object.entries(groups)) {
			for (const url of data) {
				cacheAggregateStoreDragFillId(url, attributeId, dragFillId, updateOperation);
			}
		}
	} catch (err) {
		console.error(`cacheAggregateStoreDragFillIds: ${err.message}`);

		// if error, then cache with null value so they will not be queried again
		for (const url of sourceUrls) {
			cacheAggregateStoreDragFillId(url, attributeId);
		}
	}
}

/**
 * add response to cache if the response contains a uniqueId; if not, response should not be cached
 * as data for aggregate store needs to be queried every time to be consistent
 */
export function cacheAggregateStoreResponse(key: AggregateStoreResponseKey, response: IAggregateStoreResponse): void {
	const { uniqueId } = response;
	if (uniqueId) {
		const map = cache[key] = cache[key] || {};
		map[uniqueId] = response;
	}
}

/** remove all cached aggregate store responses */
export function clearAggregateStoreCache(): void {
	cache = {};
}

/** remove all cached aggregate store responses */
export function clearAggregateStoreCaches(): void {
	clearAggregateStoreCache();
	clearAggregateStoreIdCache();
}

/** remove all cached aggregate store ids */
export function clearAggregateStoreIdCache(): void {
	cacheDragIds = {};
	cacheDragOperations = {};
}

/** remove cached responses by given condition */
export function clearAggregateStoreResponsesBy(condition: (response: IAggregateStoreResponse) => boolean): void {
	for (const [_key, map] of Object.entries(cache)) {
		for (const [responseId, response] of Object.entries(map)) {
			if (condition(response)) {
				delete map[responseId];
			}
		}
	}
}

/**
 * Remove cached responses if they contain results of the given node type
 * NOTE: this does not take into account abstract classes
 */
export function clearAggregateStoreResponsesByNodeType(nodeType: C8NodeType): void {
	clearAggregateStoreResponsesBy(({ groups }) => groups.some((group) => group.nodeType === nodeType));
}

/** remove cached responses for given attribute */
export function clearAggregateStoreResponsesForAttribute(key: AggregateStoreResponseKey): void {
	delete cache[key];
}

/** remove deleted url from cached responses */
export function clearAggregateStoreResponsesOfDeletedUrl(deletedUrl: cnl): void {
	clearAggregateStoreResponsesBy(({ groups }) => {
		groups.forEach((group) => {
			group.results = group.results?.filter(({ $id }) => $id !== deletedUrl);
		});

		// do not remove response - just remove results with deleted url
		return false;
	});
}

/** get array of aggregate store ids for given attribute whose responses are already cached */
export function getAggregateStoreCachedIds(key: AggregateStoreResponseKey): AggregateStoreUniqueId[] {
	return Object.keys(cache[key] || {});
}

/** get aggregate store response from cache for given attribute */
export function getAggregateStoreResponse(key: AggregateStoreResponseKey, responseId: AggregateStoreUniqueId): IAggregateStoreResponse | undefined {
	const map = cache[key];
	return map?.[responseId];
}

/** return aggregate store response group by groupId */
export function getAggregateResponseGroupById(res: IAggregateStoreResponse, groupId: string): IAggregateStoreGroup {
	return res.groups.find((item) => item.groupId === groupId);
}

/** return node type of groups so long as they are all the same */
export function getAggregateResponseNodeType({ groups }: IAggregateStoreResponse): C8NodeType | undefined {
	const set = new Set<C8NodeType>();

	groups.forEach(({ nodeType }) => {
		set.add(nodeType);
	});

	return set.size === 1 ? groups[0].nodeType : undefined;
}

/** return properties of aggregate store results in given group */
export function getAggregateStoreResultKeysForGroup(res: IAggregateStoreResponse, group?: IAggregateStoreGroup): string[] | undefined {
	return getAggregateStoreResultKeysForNodeType(res, group?.nodeType);
}

/** return properties of aggregate store results for given node type */
export function getAggregateStoreResultKeysForNodeType(res: IAggregateStoreResponse, nodeType?: C8NodeType): string[] | undefined {
	return res.resultKeys?.get(nodeType) || res.resultKeys?.get(DEFAULT_RESULT_KEYS_KEY) || [ATTR_NAME];
}

/** get cached aggregate store id for given node and attribute */
export function getCachedAggregateStoreDragFillId(nodeUrl: cnl, attributeId: C8AttributeId): AggregateStoreUniqueId | undefined {
	const key = getAggregateStoreDragFillIdCacheKey(nodeUrl, attributeId);
	const value = cacheDragIds[key];
	return (value === ID_IN_PROGRESS) ? ID_EMPTY : value;
}

/** return operation to use when using drag-fill on an attribute with an aggregate store */
export function getCachedAggregateStoreDragFillOperation(nodeUrl: cnl, attributeId: C8AttributeId): IAggregateStoreUpdateOperation {
	const key = getAggregateStoreDragFillIdCacheKey(nodeUrl, attributeId);
	return cacheDragOperations[key];
}

/** return true if aggregate store uniqueId is not cached and needs to be queried from the server */
export function isAggregateStoreIdUnknown(uniqueId: AggregateStoreUniqueId): boolean {
	return uniqueId === undefined;
}

/** return true if aggregate store response has some results */
export function isAggregateStoreResponseEmpty({ groups = [] }: IAggregateStoreResponse): boolean {
	return groups.every(({ results }) => !results?.length);
}

/** return true if aggregate store response does not require grouping */
export function isAggregateStoreResponseGrouped({ groups = [] }: IAggregateStoreResponse): boolean {
	return (groups.length > 1) || (groups.length === 1 && !!groups[0].labelResource);
}

/**
 * Set some global parameters for queryAggregateStore which will be used for each call
 *
 * @param newReturnQueryXML - set 'ReturnQueryXML' parameter for aggregate store query
 */
export function setQueryAggregateStoreGlobalParameters(newReturnQueryXML: boolean): void {
	returnQueryXML = newReturnQueryXML;
}

/**
 * Query aggregate store for attribute on given node, returning results of the store
 */
export async function queryAggregateStore(params: IAggregateStoreParams): Promise<IAggregateStoreResponse> {
	const {
		actionType,
		actionNodeType,
		attributeContainerIndex,
		attributeId,
		cardId,
		groupId,
		locale,
		nodeType,
		reportOperations,
		resultLimit,
		reverseAttributeId,
		reverseType,
		searchText,
		sourceUrl,
	} = params;

	const key = getAggregateStoreCacheKey(nodeType, attributeId, reverseType, reverseAttributeId);
	const cachedIds = searchText ? undefined : getAggregateStoreCachedIds(key);
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "QueryAggregateStore",
		ActionType: actionType,
		ActionNodeType: actionNodeType,
		AttributeContainerIndex: attributeContainerIndex,
		AttributeId: attributeId,
		CachedId: cachedIds,
		CardId: cardId,
		GroupId: groupId,
		Locale: locale,
		RemoveDuplicates: true,
		ReportOperations: reportOperations,
		ResultLimit: Math.min(resultLimit || Infinity, AGGREGATE_STORE_LIMIT),
		ReturnQueryXML: returnQueryXML,
		ReverseAttributeId: reverseAttributeId,
		ReverseClass: reverseType,
		SearchText: searchText,
		SourceUrl: sourceUrl,
	});

	const response = await postFormData<IAggregateStoreResponse>(formData, getFormDataConfig(queryAggregateStoreReviver));
	const { currentValues, groups, uniqueId } = response;

	if (groups) {
		// cache response only if not using delayed search - don't want to cache a subset of all results
		// also only cache response if currentValues exist - if not, then we are assuming that the
		// currentValues factor into the query (query omits current values), and so it wouldn't make
		// sense to reuse on another node that may have different current values
		if (!searchText && !!currentValues) {
			cacheAggregateStoreResponse(key, response);
		}

		return response;
	}

	// if response has no groups, then merge already cached response with new response;
	// we need to merge in order to get the values of groups and unenforcedUserDefaultsApplied
	// into the new response
	const cachedResponse = getAggregateStoreResponse(key, uniqueId);

	// ensure that currentValues in the cachedResponse are not used for this response if currentValues are missing
	// NOTE: disabledIds should be honored from the cachedResponse; if that is not working, then the way to solve it
	// is to modify the uniqueId of the aggregate store (such as is the case with GroupAssignment.__Members__)
	response.currentValues ||= [];

	return { ...cachedResponse, ...response };
}

/**
 * Query aggregate store information for attribute of the given class
 */
export async function queryAggregateStoreInfo(
	sourceClass: C8NodeType,
	attributeId: C8AttributeId,
	valueType?: C8AttributeType,
	targetClass?: C8NodeType,
	targetClassSubtypeName?: string,
	dependsOn?: C8AttributeId,
): Promise<IAggregateStoreDefinitionInfo> {
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetAggregateStoreInfo",
		SourceClass: sourceClass,
		AttributeId: attributeId,
		ValueType: valueType,
		DependsOn: dependsOn,
		Destinations: targetClass && [targetClass],
		DestinationSubtypeName: targetClassSubtypeName,
	});

	const result = await postFormData<IAggregateStoreDefinitionInfo>(formData);
	const { alternatives, dataStoreQueryMap } = result;
	
	// populate dynamic groups
	// NOTE: consider moving to server operation? https://centric8.atlassian.net/browse/C8-76051
	const firstAlternative = alternatives?.[0];
	
	if (dataStoreQueryMap && firstAlternative?.dynamic) {
		for (const [dataStoreId, value] of Object.entries(dataStoreQueryMap)) {
			const dataStoreInfo = await queryDataStoreInfo(dataStoreId);
			const nodeType = getDataStoreComponents(dataStoreId).target;

			firstAlternative.groups.push({
				dataStoreId,
				dataStoreInfo,
				nodeType,
			});
		} 
	}

	return result;
}